import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CSATReports from '../../api/csatReports';
import { downloadCsvFile } from '../../helper/downloadHelper';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { REPORTS_EVENTS } from '../../helper/AnalyticsHelper/events';

const computeDistribution = (value, total) =>
  ((value * 100) / total).toFixed(2);

const emptyMetrics = () => ({
  totalResponseCount: 0,
  ratingsCount: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
  totalSentMessagesCount: 0,
});

const normalizeRatingsCount = ratingsCount => {
  const normalized = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  if (!ratingsCount) return normalized;

  Object.entries(ratingsCount).forEach(([key, value]) => {
    const rating = Number(key);
    if (rating >= 1 && rating <= 5) {
      normalized[rating] = value || 0;
    }
  });
  return normalized;
};

let metricsRequestSequence = 0;
let listRequestSequence = 0;

export const state = {
  records: [],
  metrics: emptyMetrics(),
  uiFlags: {
    isFetching: false,
    isFetchingMetrics: false,
  },
};

export const getters = {
  getCSATResponses(_state) {
    return _state.records;
  },
  getMetrics(_state) {
    return _state.metrics;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getSatisfactionScore(_state) {
    if (!_state.metrics.totalResponseCount) {
      return 0;
    }
    return computeDistribution(
      _state.metrics.ratingsCount[4] + _state.metrics.ratingsCount[5],
      _state.metrics.totalResponseCount
    );
  },
  getResponseRate(_state) {
    if (!_state.metrics.totalSentMessagesCount) {
      return 0;
    }
    return computeDistribution(
      _state.metrics.totalResponseCount,
      _state.metrics.totalSentMessagesCount
    );
  },
  getRatingPercentage(_state) {
    if (!_state.metrics.totalResponseCount) {
      return { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    }
    return {
      1: computeDistribution(
        _state.metrics.ratingsCount[1],
        _state.metrics.totalResponseCount
      ),
      2: computeDistribution(
        _state.metrics.ratingsCount[2],
        _state.metrics.totalResponseCount
      ),
      3: computeDistribution(
        _state.metrics.ratingsCount[3],
        _state.metrics.totalResponseCount
      ),
      4: computeDistribution(
        _state.metrics.ratingsCount[4],
        _state.metrics.totalResponseCount
      ),
      5: computeDistribution(
        _state.metrics.ratingsCount[5],
        _state.metrics.totalResponseCount
      ),
    };
  },
};

export const actions = {
  get: async function getResponses({ commit }, params) {
    const requestId = ++listRequestSequence;
    commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetching: true });
    try {
      const response = await CSATReports.get(params);
      if (requestId !== listRequestSequence) return;
      commit(types.SET_CSAT_RESPONSE, response.data);
    } catch (error) {
      if (requestId !== listRequestSequence) return;
      throw error;
    } finally {
      if (requestId === listRequestSequence) {
        commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetching: false });
      }
    }
  },
  getMetrics: async function getMetrics({ commit }, params) {
    const requestId = ++metricsRequestSequence;
    commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetchingMetrics: true });
    commit(types.CLEAR_CSAT_RESPONSE_METRICS);
    try {
      const response = await CSATReports.getMetrics(params);
      if (requestId !== metricsRequestSequence) return;
      commit(types.SET_CSAT_RESPONSE_METRICS, response.data);
    } catch (error) {
      if (requestId !== metricsRequestSequence) return;
      throw error;
    } finally {
      if (requestId === metricsRequestSequence) {
        commit(types.SET_CSAT_RESPONSE_UI_FLAG, { isFetchingMetrics: false });
      }
    }
  },
  downloadCSATReports(_, params) {
    return CSATReports.download(params).then(response => {
      downloadCsvFile(params.fileName, response.data);
      AnalyticsHelper.track(REPORTS_EVENTS.DOWNLOAD_REPORT, {
        reportType: 'csat',
      });
    });
  },
};

export const mutations = {
  [types.SET_CSAT_RESPONSE_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_CSAT_RESPONSE]: MutationHelpers.set,
  [types.CLEAR_CSAT_RESPONSE_METRICS](_state) {
    _state.metrics = emptyMetrics();
  },
  [types.SET_CSAT_RESPONSE_METRICS](
    _state,
    {
      total_count: totalResponseCount,
      ratings_count: ratingsCount,
      total_sent_messages_count: totalSentMessagesCount,
    }
  ) {
    _state.metrics.totalResponseCount = totalResponseCount || 0;
    _state.metrics.ratingsCount = normalizeRatingsCount(ratingsCount);
    _state.metrics.totalSentMessagesCount = totalSentMessagesCount || 0;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
